/**
 * Domain types for the League Rankings page. Mirrors the dashboard REST DTOs
 * (`Dashboard*` in `@/api/__generated__/schemas`) but kept flat and free of any
 * generated-schema imports so the UI layer depends only on these local types.
 */

import type { AvatarFields } from "@/types/common.types";
import type { CalendarDate } from "@/ui";

/** Three-layer enum: refresh cadence for the automatic-update schedule. */
export const RankingRefreshRateValues = ["every_week", "every_month", "custom"] as const;
export type RankingRefreshRate = (typeof RankingRefreshRateValues)[number];

/**
 * `updateEvery` option value set for a WEEKLY cadence. The API expects a
 * capitalised English day name (e.g. `Monday`) — see the schedule DTO.
 */
export const RANKING_UPDATE_DAY_OF_WEEK_VALUES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;
export type RankingUpdateDayOfWeek = (typeof RANKING_UPDATE_DAY_OF_WEEK_VALUES)[number];

/**
 * Presentation of a snapshot's `isPublic` boolean as a two-state badge select.
 * UI-only — mapped back to the `isPublic` boolean at the mutation boundary.
 */
export const LeagueRankingVisibilityValues = ["public", "hidden"] as const;
export type LeagueRankingVisibility = (typeof LeagueRankingVisibilityValues)[number];

/** `updateEvery` option value set for a MONTHLY cadence. */
export const RANKING_UPDATE_MONTH_ANCHOR_VALUES = [
  "first_day_of_month",
  "last_day_of_month",
] as const;
export type RankingUpdateMonthAnchor = (typeof RANKING_UPDATE_MONTH_ANCHOR_VALUES)[number];

/**
 * Athlete's linked club, shaped for the shared `ClubCell` (logo + name). Null
 * when the athlete has no club.
 */
export type LeagueRankingClubRef = {
  id: string;
  name: string;
  logoFile: { url: string | null } | null;
  legacyLogoUrl: string | null;
};

/** Athlete reference on an individual ranking row. */
export type LeagueRankingUserRef = {
  id: string;
  name: string | null;
  surname: string | null;
  avatar: string | null;
  nationality: string | null;
  club: LeagueRankingClubRef | null;
  birthDate: CalendarDate | null;
};

/** Team reference on a team ranking row. */
export type LeagueRankingTeamRef = {
  id: string;
  name: string | null;
  logo: string | null;
};

/** A single leaderboard row for a (league, ranking category, date) snapshot. */
export type LeagueRankingListItem = {
  id: string;
  place: number | null;
  previousPlace: number | null;
  points: number | null;
  previousPoints: number | null;
  tournaments: number | null;
  thirdPartyPoints: string | null;
  isPublic: boolean;
  /** Snapshot date (`DD-MM-YYYY`). */
  date: string;
  athlete: LeagueRankingUserRef | null;
  team: LeagueRankingTeamRef | null;
};

/**
 * A user acting on a league-ranking record (generated / created / updated it),
 * adapted to the shared `AvatarFields` shape the `UpdatedByUser` / `UserAvatar`
 * components expect. The backend `UserRef` avatar URL maps into `legacyFileUrl`.
 */
export type LeagueRankingActor = AvatarFields & {
  id: string;
  name: string | null;
  surname: string | null;
};

/**
 * User who generated a snapshot. Null when the snapshot was generated
 * automatically by the scheduler. Alias of {@link LeagueRankingActor}.
 */
export type LeagueRankingGeneratedBy = LeagueRankingActor;

/** A generated snapshot in the History tab. */
export type LeagueRankingHistoryItem = {
  id: string;
  rankingCategory: { id: string; name: string | null };
  /** Snapshot date (`DD-MM-YYYY`). */
  date: string;
  /** Original creation time of the snapshot (preserved across regenerations). */
  createdAt: Date;
  /** When the snapshot was last regenerated. */
  lastUpdated: Date;
  athletesCount: number;
  isPublic: boolean;
  /** True when the snapshot was generated by the automatic scheduler. */
  isAutomatic: boolean;
  /** Who generated the latest regeneration, or null when automatic. */
  generatedBy: LeagueRankingGeneratedBy | null;
  /**
   * The automatic-update rule (`lrr_…` public id + name) that generated this
   * snapshot, when produced by the cron scheduler firing a specific rule. Null
   * for manual generations and legacy snapshots.
   */
  generatedByRule: { id: string; name: string | null } | null;
};

/** One contributing tournament result inside the points-detail breakdown. */
export type LeagueRankingPointResult = {
  id: string;
  place: number | null;
  points: number | null;
  counted: boolean;
  activeFrom: string | null;
  /** When this result ages out of the ranking. Null when it never expires. */
  expiresAt: string | null;
  /** Numeric id of the ranking-point block this result was scored against, or null. */
  rankingPointId: number | null;
  tournament: {
    id: string;
    internalId: number;
    title: string | null;
    startDate: string | null;
    endDate: string | null;
  };
  tournamentCategory: { id: string; name: string | null };
};

/** One international / third-party points line inside the points breakdown. */
export type LeagueRankingThirdPartyPoint = {
  name: string;
  multiplier: number | null;
  points: number | null;
  totalPoints: number | null;
};

/** The full points breakdown behind a single leaderboard row. */
export type LeagueRankingPointsDetail = {
  totalPoints: number | null;
  thirdPartyPoints: string | null;
  thirdPartyPointsBreakdown: LeagueRankingThirdPartyPoint[];
  results: LeagueRankingPointResult[];
};

/** Explicit date/time pair for a `custom` cadence. */
export type LeagueRankingCustomScheduleItem = {
  date: string;
  time: string;
};

/** The automatic-update schedule for a league's rankings. */
export type LeagueRankingSchedule = {
  isEnabled: boolean;
  rankingCategories: { id: string; name: string | null }[];
  refreshRate: RankingRefreshRate | null;
  updateEvery: string | null;
  time: string | null;
  customSchedule: LeagueRankingCustomScheduleItem[] | null;
};

/** A ranking category assigned to an automatic-update rule (`rct_` ref). */
export type LeagueRankingRuleCategoryRef = { id: string; name: string | null };

/**
 * One automatic-update rule: a refresh schedule + the ranking categories it
 * refreshes + an optional human-facing name. A league holds at most
 * `MAX_LEAGUE_RANKING_RULES` of these. `refreshRate` is null only for legacy /
 * migrated rows that predate the cadence field.
 */
export type LeagueRankingRule = {
  /** Rule public id (`lrr_…`). Address a rule by this id in the rules CRUD. */
  id: string;
  name: string | null;
  refreshRate: RankingRefreshRate | null;
  updateEvery: string | null;
  time: string | null;
  customSchedule: LeagueRankingCustomScheduleItem[] | null;
  rankingCategories: LeagueRankingRuleCategoryRef[];
  /** Who created the rule, or null for migrated / legacy rows. */
  createdBy: LeagueRankingActor | null;
  /** When the rule was created. */
  createdAt: Date;
  /** Who last updated the rule (also set on create), or null for legacy rows. */
  updatedBy: LeagueRankingActor | null;
  /** When the rule was last updated. */
  updatedAt: Date;
};

/**
 * Sortable columns of the Rankings leaderboard table. Mapped to the dashboard
 * `orderBy` enum by `sortDescriptorToLeagueRankingsOrderBy`.
 */
export const LeagueRankingSortableColumnValues = ["position", "points"] as const;
export type LeagueRankingSortableColumn = (typeof LeagueRankingSortableColumnValues)[number];

/**
 * Selectable columns of the league-rankings export, in the canonical order the
 * generated file lays them out. Sent as the `columns` param — omitted means
 * "every column".
 */
export const LeagueRankingExportColumnValues = [
  "rankingCategory",
  "date",
  "place",
  "previousPlace",
  "name",
  "surname",
  "team",
  "nation",
  "birthDate",
  "club",
  "points",
  "previousPoints",
  "tournaments",
] as const;
export type LeagueRankingExportColumn = (typeof LeagueRankingExportColumnValues)[number];
